import {useMemo} from 'react';

import {WORKSPACE_PLACEHOLDER_NAME} from '../../../state/workspaces/displayNames';
import type {WorkspaceViews} from '../../../state/workspaces/workspaceViewListSelector';
import type {NamedWorkspaceMenuItem, SortOption} from '../types';
import {getMenuItems} from './useMenuItems';

type OwnershipSortOptions = {
  ownedByViewer: SortOption;
  ownedByOthers: SortOption;
};

type WorkspaceViewItemsByOwnerArgs = {
  personalWorkspaces: WorkspaceViews;
  sharedViews: WorkspaceViews;
  viewerId: string | undefined;
  sortOptions: OwnershipSortOptions;
  searchQuery?: string;
};

type WorkspaceViewItemsSection = {
  items: NamedWorkspaceMenuItem[];
  totalCount: number;
};

type WorkspaceViewItemsByOwner = {
  ownedByViewer: WorkspaceViewItemsSection;
  ownedByOthers: WorkspaceViewItemsSection;
};

const sortItems = (items: NamedWorkspaceMenuItem[], sortOption: SortOption) => {
  return items.sort((a, b) => {
    switch (sortOption) {
      case 'lastSaved':
        return (
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
      case 'lastCreated':
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      case 'alphabetical': {
        const nameA = a.displayName || WORKSPACE_PLACEHOLDER_NAME;
        const nameB = b.displayName || WORKSPACE_PLACEHOLDER_NAME;
        return nameA.localeCompare(nameB);
      }
      default:
        return 0;
    }
  });
};

const filterItems = (items: NamedWorkspaceMenuItem[], searchQuery: string) => {
  if (!searchQuery) {
    return items;
  }

  const normalizedQuery = searchQuery.toLowerCase();
  return items.filter(item => {
    const displayName = item.displayName || WORKSPACE_PLACEHOLDER_NAME;
    return (
      displayName.toLowerCase().includes(normalizedQuery) ||
      item.username.toLowerCase().includes(normalizedQuery)
    );
  });
};

const prioritizePersonalWorkspace = (items: NamedWorkspaceMenuItem[]) => {
  const personalWorkspace = items.find(item => item.optionType === 'personal');
  if (personalWorkspace == null) {
    return items;
  }

  return [
    personalWorkspace,
    ...items.filter(item => item !== personalWorkspace),
  ];
};

export const useWorkspaceViewItemsByOwner = ({
  personalWorkspaces,
  sharedViews,
  viewerId,
  sortOptions,
  searchQuery = '',
}: WorkspaceViewItemsByOwnerArgs): WorkspaceViewItemsByOwner => {
  return useMemo(() => {
    const allItems = [
      ...getMenuItems('personal', Object.values(personalWorkspaces), undefined),
      ...getMenuItems('shared', Object.values(sharedViews), undefined),
    ];
    const ownedByViewer = allItems.filter(
      item => item.createdBy.id === viewerId
    );
    const ownedByOthers = allItems.filter(
      item => item.createdBy.id !== viewerId
    );

    return {
      ownedByViewer: {
        items: filterItems(
          prioritizePersonalWorkspace(
            sortItems(ownedByViewer, sortOptions.ownedByViewer)
          ),
          searchQuery
        ),
        totalCount: ownedByViewer.length,
      },
      ownedByOthers: {
        items: filterItems(
          sortItems(ownedByOthers, sortOptions.ownedByOthers),
          searchQuery
        ),
        totalCount: ownedByOthers.length,
      },
    };
  }, [
    personalWorkspaces,
    searchQuery,
    sharedViews,
    sortOptions.ownedByOthers,
    sortOptions.ownedByViewer,
    viewerId,
  ]);
};
